import { useEffect } from "react";
import useSWR, { mutate as mutateSWR } from "swr";
import { fetcher, useAPIMutation } from "@/api/api";
import {
  IFRechargeLotteryDrawRsp,
  IFRechargeLotteryInfoRsp,
} from "@/api/apiRsp/IFRechargeLotteryRsp";
import { useGlobalState } from "@/store/commonStore";
import { useWsUpdateBanblce } from "@/store/hallSocket";
import { isRechargeLotteryInfoRefreshTrade } from "./rechargeLotteryTrade";

const RECHARGE_LOTTERY_INFO_REFRESH_DELAY = 1000;
let rechargeLotteryInfoRefreshTimer: number | undefined;
let rechargeLotteryInfoRefreshToken = 0;

const scheduleRechargeLotteryInfoRefresh = (
  refresh: () => void | Promise<unknown>
) => {
  const token = rechargeLotteryInfoRefreshToken + 1;
  rechargeLotteryInfoRefreshToken = token;

  if (rechargeLotteryInfoRefreshTimer !== undefined) {
    window.clearTimeout(rechargeLotteryInfoRefreshTimer);
  }

  rechargeLotteryInfoRefreshTimer = window.setTimeout(() => {
    if (token !== rechargeLotteryInfoRefreshToken) {
      return;
    }

    rechargeLotteryInfoRefreshTimer = undefined;
    void refresh();
  }, RECHARGE_LOTTERY_INFO_REFRESH_DELAY);
};

const RechargeLotteryInfoAPI = {
  path: "/api/rechargeLottery/info",
  type: "get",
  needLogin: true,
};

export const getRechargeLotteryInfoSWRKey = (
  token?: string,
  isLogin = false
) => (isLogin && token ? `rechargeLotteryInfo?${token}` : null);

export const refreshRechargeLotteryInfoCache = (
  token?: string,
  isLogin = false
) => {
  const swrKey = getRechargeLotteryInfoSWRKey(token, isLogin);
  if (!swrKey) {
    return Promise.resolve(undefined);
  }

  return mutateSWR<IFRechargeLotteryInfoRsp>(
    swrKey,
    () =>
      fetcher<IFRechargeLotteryInfoRsp>(RechargeLotteryInfoAPI, {
        arg: null,
      }),
    { revalidate: false }
  );
};

export const scheduleRechargeLotteryInfoCacheRefresh = (
  token?: string,
  isLogin = false
) => {
  scheduleRechargeLotteryInfoRefresh(() =>
    refreshRechargeLotteryInfoCache(token, isLogin)
  );
};

export function useRechargeLotteryInfo() {
  const { isLogin, token } = useGlobalState();
  const swrKey = getRechargeLotteryInfoSWRKey(token, isLogin);
  const { data, isLoading, isValidating, error, mutate } = useSWR<
    IFRechargeLotteryInfoRsp
  >(
    swrKey,
    () =>
      fetcher<IFRechargeLotteryInfoRsp>(RechargeLotteryInfoAPI, {
        arg: null,
      }),
    { revalidateOnMount: true }
  );
  const { data: balanceChangeData } = useWsUpdateBanblce(false);

  useEffect(() => {
    if (!isRechargeLotteryInfoRefreshTrade(balanceChangeData?.tradeModeCode)) {
      return undefined;
    }

    scheduleRechargeLotteryInfoCacheRefresh(token, isLogin);
    return undefined;
  }, [balanceChangeData, isLogin, token]);

  return {
    data,
    revalidate: mutate,
    error,
    isLoading,
    isValidating,
  };
}

export function useRechargeLotteryDraw() {
  const { trigger, data, error, isMutating, reset } = useAPIMutation<
    IFRechargeLotteryDrawRsp,
    null
  >({
    path: "/api/rechargeLottery/draw",
    type: "get",
    needLogin: true,
  });

  return {
    trigger,
    data,
    error,
    isMutating,
    reset,
  };
}
